import { Box, CircularProgress, CircularProgressLabel, Flex, Heading, List, ListItem, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';

import { CAPTION_FONT_SIZE, CARD_BORDER_COLOR, GRADUATE_OUTCOMES_PERCENT, SALARY_DISCLAIMER } from '../constants';
import type { Outcomes } from '../models/outcomes';

export type OutcomesSectionProps = {
  outcomes: Outcomes;
};

/**
 * Chakra draws the arc at a fixed r=42 in a 100-unit viewBox, so the rendered
 * ring is `size * (84 + thickness) / 100` — not `size`. These values land the
 * outer diameter on the 104px the Figma module specifies.
 */
const GAUGE_SIZE = '112px';
const GAUGE_THICKNESS = '8px';

type SalaryTier = {
  bg: string;
  label: string;
  value: string;
  years: string;
};

function SalaryTierItem({ tier, align }: { tier: SalaryTier; align: 'flex-start' | 'center' | 'flex-end' }) {
  return (
    <Flex alignItems={align} flexDirection='column' gap={2}>
      <Box
        bg={tier.bg}
        borderRadius='4px'
        py={1}
        px={{
          base: 2,
          md: 3,
        }}
      >
        <Text
          color='supportGray.100'
          fontWeight='700'
          lineHeight='md'
          whiteSpace='nowrap'
          fontSize={{
            base: 'md',
            md: 'lg',
          }}
        >
          {tier.value}
        </Text>
      </Box>
      <Text color='supportGray.100' fontSize='sm' fontWeight='600' lineHeight='sm' whiteSpace='nowrap'>
        {tier.label}
      </Text>
      <Text color='supportGray.60' fontSize={CAPTION_FONT_SIZE} lineHeight='sm' whiteSpace='nowrap'>
        {tier.years}
      </Text>
    </Flex>
  );
}

function GraduateOutcomesGauge() {
  return (
    <Flex
      alignItems='center'
      bg='supportGray.10'
      borderColor={CARD_BORDER_COLOR}
      borderStyle='solid'
      flexDirection='column'
      flexShrink={0}
      gap={2}
      justifyContent='center'
      padding={5}
      textAlign='center'
      borderBottomWidth={{
        base: '1px',
        md: '0',
      }}
      borderRightWidth={{
        base: '0',
        md: '1px',
      }}
      width={{
        base: '100%',
        md: '270px',
      }}
    >
      <CircularProgress
        aria-label={`${GRADUATE_OUTCOMES_PERCENT}% of Udacity graduates reported a positive career outcome`}
        color='primary.blue'
        size={GAUGE_SIZE}
        thickness={GAUGE_THICKNESS}
        trackColor='supportBlue.20'
        value={GRADUATE_OUTCOMES_PERCENT}
        capIsRound
      >
        <CircularProgressLabel
          color='primary.blue'
          fontFamily='heading'
          fontSize='2xl'
          fontWeight='600'
          letterSpacing='-0.62px'
        >
          {GRADUATE_OUTCOMES_PERCENT}%
        </CircularProgressLabel>
      </CircularProgress>
      <Text color='supportGray.100' fontSize='sm' lineHeight='md'>
        of Udacity graduates reported a positive career outcome
      </Text>
      <Text
        color='supportGray.60'
        fontSize={CAPTION_FONT_SIZE}
        fontStyle='italic'
        lineHeight='sm'
        sx={{ textWrap: 'balance' }}
      >
        Source: Udacity’s Q2 2026 Learner Outcome Survey
      </Text>
    </Flex>
  );
}

type OutcomesStatCardProps = {
  outcomes: Outcomes;
};

function OutcomesStatCard({ outcomes }: OutcomesStatCardProps) {
  const tiers: SalaryTier[] = [
    { bg: 'blue.100', value: outcomes.tcBeginner, label: 'Entry-level', years: '0–2 yrs' },
    { bg: 'grey.100', value: outcomes.tcMidCareer, label: 'Mid-career', years: '3–9 yrs' },
    { bg: 'orange.100', value: outcomes.tcExperienced, label: 'Experienced', years: '10+ yrs' },
  ];

  return (
    <Flex
      border='1px solid'
      borderColor={CARD_BORDER_COLOR}
      borderRadius='8px'
      overflow='hidden'
      width='100%'
      flexDirection={{
        base: 'column',
        md: 'row',
      }}
    >
      <GraduateOutcomesGauge />
      <Flex
        flex={1}
        flexDirection='column'
        gap={3}
        minWidth={0}
        paddingY={5}
        paddingX={{
          base: 5,
          md: 7,
        }}
      >
        <Text color='supportGray.100' fontSize='md' lineHeight='md'>
          This Nanodegree builds toward roles like
        </Text>
        <Flex alignItems='baseline' flexWrap='wrap' gap={2}>
          <Heading as='p' color='supportGray.100' size='heading-7'>
            {outcomes.associatedRole}
          </Heading>
          <Text color='supportGray.60' fontSize='sm' fontWeight='600' lineHeight='sm'>
            · U.S. salaries
          </Text>
        </Flex>
        <Box
          bgGradient='linear(to right, primary.blue, primary.purple, primary.orange)'
          borderRadius='full'
          h='8px'
          w='100%'
        />
        <Flex alignItems='flex-start' gap={2} justifyContent='space-between' width='100%'>
          <SalaryTierItem align='flex-start' tier={tiers[0]} />
          <SalaryTierItem align='center' tier={tiers[1]} />
          <SalaryTierItem align='flex-end' tier={tiers[2]} />
        </Flex>
        <Text color='supportGray.60' fontSize={CAPTION_FONT_SIZE} fontStyle='italic' lineHeight='sm'>
          {SALARY_DISCLAIMER}
        </Text>
      </Flex>
    </Flex>
  );
}

function CompaniesHiring({ companies }: { companies: string[] }) {
  if (companies.length === 0) return null;

  return (
    <Flex
      bg='supportGray.10'
      border='1px solid'
      borderColor={CARD_BORDER_COLOR}
      borderRadius='8px'
      flexDirection='column'
      gap={3}
      paddingX={6}
      paddingY={4}
      width='100%'
    >
      <Text
        color='supportGray.60'
        fontSize={CAPTION_FONT_SIZE}
        fontWeight='600'
        letterSpacing='1.15px'
        lineHeight='sm'
        textTransform='uppercase'
      >
        Companies hiring for these skills
      </Text>
      <List display='flex' flexWrap='wrap' gap={2}>
        {companies.map((name) => (
          <ListItem
            key={name}
            bg='white'
            border='1px solid'
            borderColor={CARD_BORDER_COLOR}
            borderRadius='4px'
            color='supportGray.100'
            fontSize='sm'
            fontWeight='600'
            lineHeight='sm'
            px={4}
            py={2}
          >
            {name}
          </ListItem>
        ))}
      </List>
    </Flex>
  );
}

export function OutcomesSection({ outcomes }: OutcomesSectionProps) {
  return (
    <Box as='section'>
      <ContentContainer display='flex' flexDirection='column' gap={6}>
        <Heading
          as='h2'
          size={{
            base: 'heading-6',
            md: 'heading-5',
          }}
        >
          Career Impact
        </Heading>
        <Flex flexDirection='column' gap={4}>
          <OutcomesStatCard outcomes={outcomes} />
          <CompaniesHiring companies={outcomes.companiesHiring} />
        </Flex>
      </ContentContainer>
    </Box>
  );
}

/**
 * Resolves the outcomes for a program at build time. The lookup module is
 * imported dynamically so `outcomes.json` — which holds the outcomes for all
 * Project-First PLP programs — stays out of the client bundle and only the
 * current program's outcomes are serialized into the page props.
 */
export async function transformStaticOutcomesSubsectionProps({ programKey }: { programKey: string }) {
  const { getOutcomes } = await import('../utils/get-outcomes');
  const outcomes = getOutcomes(programKey);

  if (!outcomes) {
    return null;
  }

  return {
    outcomes,
  };
}

export type StaticOutcomesSubsectionProps = Awaited<ReturnType<typeof transformStaticOutcomesSubsectionProps>>;
